import React, { useState, useEffect } from 'react';
import {
  Table, Button, Badge, Modal, Form, Row, Col, Card, Alert, Accordion
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaDownload, FaTrash, FaUpload } from 'react-icons/fa';
import axios from 'axios';

const UaeCrossBorderTransfersTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities }) => {
  const [transfers, setTransfers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [newTransfer, setNewTransfer] = useState({
    type: 'International Transfer',
    source: '',
    destination: '',
    entity: '',
    legalBasis: '',
    dataCategories: [],
    adequateProtection: false,
    contractualSafeguards: false,
    tdpAuthorization: false,
    dataTransferAgreementKey: ''
  });

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const LEGAL_BASIS_OPTIONS = [
    'Explicit Consent of Data Subject',
    'Contractual Necessity',
    'Legal Obligation Compliance',
    'Vital Interests Protection',
    'Public Interest Task',
    'Legitimate Interest',
    'Data Transfer Agreement',
    'Binding Corporate Rules',
    'International Agreement',
    'TDP Authorization'
  ];

  // GCC countries with similar data protection frameworks
  const GCC_COUNTRIES = [
    'United Arab Emirates', 'Saudi Arabia', 'Kuwait', 'Qatar', 
    'Bahrain', 'Oman'
  ];

  // Countries with adequate data protection levels recognized by UAE
  const ADEQUATE_PROTECTION_COUNTRIES = [
    'European Union Member States', 'United Kingdom', 'Switzerland',
    'Canada', 'New Zealand', 'Singapore', 'Japan', 'South Korea',
    'Australia', 'Israel'
  ];

  useEffect(() => {
    fetchTransfers();
  }, []);

  const fetchTransfers = async () => {
    try {
      const res = await axios.get('https://ioec6c1z76.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/cross-border-transfers/list', { headers });
      setTransfers(res.data);
      calculateComplianceScore(res.data);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  const calculateComplianceScore = (transfersList) => {
    if (transfersList.length === 0) {
      onScoreUpdate(100); // No transfers = no risk = perfect score
      return;
    }

    let score = 100;
    const totalTransfers = transfersList.length;

    // Check each transfer for UAE compliance
    transfersList.forEach(transfer => {
      const destinationCountry = transfer.destination;
      const isGCC = GCC_COUNTRIES.includes(destinationCountry);
      const hasAdequateProtection = ADEQUATE_PROTECTION_COUNTRIES.some(country => 
        destinationCountry.includes(country) || country.includes(destinationCountry)
      );
      
      if (isGCC) {
        // GCC transfer - generally compliant due to similar frameworks
        return;
      }

      if (hasAdequateProtection && transfer.adequateProtection) {
        // Adequate protection available and noted - compliant
        return;
      }

      if (!transfer.legalBasis) {
        score -= 25; // No legal basis = major penalty under UAE law
      } else if (transfer.legalBasis === 'Data Transfer Agreement' && !transfer.contractualSafeguards) {
        score -= 15; // Data Transfer Agreement selected but not properly documented
      } else if (transfer.legalBasis === 'TDP Authorization' && !transfer.tdpAuthorization) {
        score -= 20; // TDP authorization required but not obtained
      }

      if (!transfer.dataCategories || transfer.dataCategories.length === 0) {
        score -= 10; // No data categories specified
      }

      // Additional UAE-specific checks
      if (!transfer.adequateProtection && !transfer.contractualSafeguards && 
          !['Explicit Consent of Data Subject', 'Contractual Necessity'].includes(transfer.legalBasis)) {
        score -= 15; // Insufficient safeguards for third country transfer
      }
    });

    onScoreUpdate(Math.max(0, Math.round(score)));
  };

  const handleSave = async () => {
    try {
      const payload = { ...newTransfer };
      
      if (editingTransfer) {
        await axios.put(`https://1s040yom1k.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/cross-border-transfers/update/${editingTransfer.id}`, payload, { headers });
        await logActivity(`Updated cross-border data transfer to ${payload.destination}`, "warning");
      } else {
        await axios.post('https://k50tky2d77.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/cross-border-transfers/create', payload, { headers });
        await logActivity(`Added new cross-border data transfer to ${payload.destination}`, "info");
      }

      showNotification('Data transfer record saved successfully!');
      fetchTransfers();
      resetForm();
    } catch (error) {
      console.error('Error saving transfer:', error);
      showNotification('Error saving data transfer record', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this data transfer record?')) {
      try {
        await axios.delete(`https://4a9piu0brc.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/cross-border-transfers/delete/${id}`, { headers });
        await logActivity('Deleted cross-border data transfer record', "danger");
        showNotification('Data transfer record deleted successfully!');
        fetchTransfers();
      } catch (error) {
        console.error('Error deleting transfer:', error);
        showNotification('Error deleting data transfer record', 'danger');
      }
    }
  };

  const resetForm = () => {
    setNewTransfer({
      type: 'International Transfer',
      source: '',
      destination: '',
      entity: '',
      legalBasis: '',
      dataCategories: [],
      adequateProtection: false,
      contractualSafeguards: false,
      tdpAuthorization: false,
      dataTransferAgreementKey: ''
    });
    setEditingTransfer(null);
    setShowModal(false);
  };

  const downloadDataTransferAgreement = async (transferId, fileName) => {
    try {
      const res = await axios.get(`https://5g08n1wgyk.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/cross-border-transfers/download-agreement/${transferId}`, 
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `DataTransferAgreement_${transferId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      await logActivity(`Downloaded Data Transfer Agreement for transfer ${transferId}`, "primary");
    } catch (error) {
      console.error('Error downloading Data Transfer Agreement:', error);
      showNotification('Error downloading Data Transfer Agreement document', 'danger');
    }
  };

  const logActivity = async (message, badge = "info") => {
    console.log('🔹 Starting logActivity in UAE Cross-Border Transfers');
    console.log('📥 Input parameters:', { message, badge });
    
    try {
      const payload = {
        action: badge,
        module: "UAE Cross Border Data Transfers",
        details: message
      };
      
      console.log('📦 Request payload:', payload);
      console.log('📋 Headers to send:', headers);
      
      const response = await axios.post(
        "https://6x7bayihnj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activity-log/save",
        payload,     
        { headers }
      );
      
      console.log('✅ UAE cross-border transfer activity logged successfully');
      console.log('📊 Response:', response.data);
      
      if (refreshActivities) {
        await refreshActivities();
      }
      
    } catch (err) {
      console.error('❌ Error logging UAE cross-border transfer activity:', err);
      
      if (err.response) {
        console.error('🔥 Server Error Details:');
        console.error('Status:', err.response.status);
        console.error('Data:', err.response.data);
      }
      
      console.warn("Failed to log activity:", err.message);
    }
  };

  return (
    <>
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Cross-Border Personal Data Transfers</Accordion.Header>
          <Accordion.Body>
            <Alert variant="info" className="mb-3">
              <strong>UAE Personal Data Protection Law Compliance:</strong> All cross-border transfers must comply with Federal Decree-Law No. 45 of 2021. 
              Ensure adequate protection measures are in place for transfers outside the UAE.
            </Alert>

            <Row className="mb-3">
              <Col>
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  <FaPlus /> Register New Data Transfer
                </Button>
              </Col>
            </Row>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Source → Destination</th>
                  <th>Organization</th>
                  <th>Legal Basis</th>
                  <th>Data Categories</th>
                  <th>Protection Status</th>
                  <th>Agreement</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((transfer) => (
                  <tr key={transfer.id}>
                    <td>#{transfer.id}</td>
                    <td>
                      <Badge bg="info">{transfer.type}</Badge>
                    </td>
                    <td>{transfer.source} → {transfer.destination}</td>
                    <td>{transfer.entity}</td>
                    <td>{transfer.legalBasis}</td>
                    <td>
                      {transfer.dataCategories?.map((cat, idx) => (
                        <Badge key={idx} bg="secondary" className="me-1">{cat}</Badge>
                      ))}
                    </td>
                    <td>
                      <Badge bg={
                        GCC_COUNTRIES.includes(transfer.destination) ? 'success' :
                        ADEQUATE_PROTECTION_COUNTRIES.some(country => 
                          transfer.destination.includes(country) || country.includes(transfer.destination)
                        ) ? 'primary' : 'warning'
                      }>
                        {GCC_COUNTRIES.includes(transfer.destination) ? 'GCC Member' :
                         ADEQUATE_PROTECTION_COUNTRIES.some(country => 
                           transfer.destination.includes(country) || country.includes(transfer.destination)
                         ) ? 'Adequate Protection' : 'Requires Safeguards'}
                      </Badge>
                    </td>
                    <td>
                      {transfer.dataTransferAgreementKey ? (
                        <Button size="sm" variant="outline-primary" 
                                onClick={() => downloadDataTransferAgreement(transfer.id, transfer.dataTransferAgreementKey)}>
                          <FaDownload /> Download
                        </Button>
                      ) : '-'}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-warning" className="me-1" 
                              onClick={() => { setEditingTransfer(transfer); setNewTransfer(transfer); setShowModal(true); }}>
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(transfer.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTransfer ? 'Edit' : 'Register'} Cross-Border Data Transfer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Transfer Type</Form.Label>
                  <Form.Select
                    value={newTransfer.type}
                    onChange={(e) => setNewTransfer({...newTransfer, type: e.target.value})}
                  >
                    <option value="International Transfer">International Transfer</option>
                    <option value="Third Country Processing">Third Country Processing</option>
                    <option value="Joint Processing">Joint Processing</option>
                    <option value="Processor Transfer">Processor Transfer</option>
                    <option value="Cloud Storage Transfer">Cloud Storage Transfer</option>
                    <option value="Outsourcing Transfer">Outsourcing Transfer</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Receiving Organization</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransfer.entity}
                    onChange={(e) => setNewTransfer({...newTransfer, entity: e.target.value})}
                    placeholder="Name of receiving organization"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Country/Region</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransfer.source}
                    onChange={(e) => setNewTransfer({...newTransfer, source: e.target.value})}
                    placeholder="e.g., United Arab Emirates"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Destination Country</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransfer.destination}
                    onChange={(e) => setNewTransfer({...newTransfer, destination: e.target.value})}
                    placeholder="e.g., United States"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Legal Basis for Transfer</Form.Label>
              <Form.Select
                value={newTransfer.legalBasis}
                onChange={(e) => setNewTransfer({...newTransfer, legalBasis: e.target.value})}
              >
                <option value="">Select Legal Basis</option>
                {LEGAL_BASIS_OPTIONS.map((basis) => (
                  <option key={basis} value={basis}>{basis}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categories of Personal Data Being Transferred</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newTransfer.dataCategories?.join(', ') || ''}
                onChange={(e) => setNewTransfer({...newTransfer, dataCategories: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                placeholder="e.g., Customer contact details, Transaction data, Employee records, Financial information"
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="Adequate data protection available"
                  checked={newTransfer.adequateProtection}
                  onChange={(e) => setNewTransfer({...newTransfer, adequateProtection: e.target.checked})}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="Contractual safeguards implemented"
                  checked={newTransfer.contractualSafeguards}
                  onChange={(e) => setNewTransfer({...newTransfer, contractualSafeguards: e.target.checked})}
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="checkbox"
                  label="TDP authorization obtained"
                  checked={newTransfer.tdpAuthorization}
                  onChange={(e) => setNewTransfer({...newTransfer, tdpAuthorization: e.target.checked})}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>
            {editingTransfer ? 'Update' : 'Register'} Transfer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UaeCrossBorderTransfersTab;
